import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, MapPin, ClipboardCopy, Clock, PlusCircle, Truck } from "lucide-react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"
import { toast } from "sonner"

export default function UpdateTracking() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const adminAuth = useAdminAuth()
  
  // Safety check for admin context
  if (!adminAuth || !adminAuth.getOrderById || !adminAuth.addShipment) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-gray-500">Admin context not available. Please refresh the page.</p>
        <Button onClick={() => navigate("/admin/orders/list")} variant="outline">
          Back to Orders
        </Button>
      </div>
    )
  }

  const { getOrderById, addShipment } = adminAuth

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [addingShipment, setAddingShipment] = useState(false)
  
  // Form state
  const [trackingNumber, setTrackingNumber] = useState("")
  const [courier, setCourier] = useState("")
  const [courierService, setCourierService] = useState("")
  const [estimatedDelivery, setEstimatedDelivery] = useState("")

  // Fetch order data
  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        navigate("/admin/orders/list")
        return
      }

      setLoading(true)
      try {
        const orderData = await getOrderById(orderId)
        // Ensure we have the order data in the expected format
        if (orderData) {
          setOrder(orderData)
        } else {
          throw new Error("Order data is empty")
        }
      } catch (error) {
        console.error("Error fetching order:", error)
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to load order details"
        toast.error(errorMessage)
        // Only navigate if it's a not found error, otherwise stay on page to show error
        if (error?.response?.status === 404) {
          navigate(`/admin/orders/list`)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchOrder()
  }, [orderId, getOrderById, navigate])

  const handleAddShipment = async (e) => {
    e.preventDefault()
    
    if (!trackingNumber.trim() || !courier.trim()) {
      toast.error("Tracking number and courier are required")
      return
    }

    // Use orderId from URL params, or fallback to order.orderId if available
    const currentOrderId = orderId || order?.orderId || order?.order_id
    if (!currentOrderId) {
      toast.error("Order ID is missing. Please refresh the page.")
      return
    }

    setAddingShipment(true)
    try {
      await addShipment(currentOrderId, {
        tracking_number: trackingNumber.trim(),
        courier: courier.trim(),
        courier_service: courierService.trim() || null,
        estimated_delivery: estimatedDelivery || null,
      })
      
      // Clear form
      setTrackingNumber("")
      setCourier("")
      setCourierService("")
      setEstimatedDelivery("")
      
      // Refresh order data using the same orderId
      const orderData = await getOrderById(currentOrderId)
      setOrder(orderData)
      
      toast.success("Shipment added successfully")
    } catch (error) {
      console.error("Failed to add shipment:", error)
      // Error message is already shown by addShipment function
    } finally {
      setAddingShipment(false)
    }
  }

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A"
    const date = new Date(isoString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  }

  // Combine shipments and order history for timeline
  const getTrackingEvents = () => {
    const events = []
    
    // Add shipments
    if (order?.shipments && order.shipments.length > 0) {
      order.shipments.forEach((shipment) => {
        events.push({
          type: "shipment",
          timestamp: shipment.shipped_at,
          title: `Shipment Created - ${shipment.courier}`,
          description: `Tracking Number: ${shipment.tracking_number}`,
          location: shipment.courier_service || shipment.courier,
          estimatedDelivery: shipment.estimated_delivery,
        })
      })
    }
    
    // Add order status history
    if (order?.history && order.history.length > 0) {
      order.history.forEach((historyItem) => {
        events.push({
          type: "status",
          timestamp: historyItem.changed_at,
          title: `Status: ${historyItem.status}`,
          description: historyItem.note || `Order status changed to ${historyItem.status}`,
          location: null,
        })
      })
    }
    
    // Sort by timestamp (newest first)
    return events.sort((a, b) => {
      const dateA = new Date(a.timestamp)
      const dateB = new Date(b.timestamp)
      return dateB - dateA
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-gray-500">Order not found.</p>
        <Link to="/admin/orders/list">
          <Button variant="outline">Back to Orders</Button>
        </Link>
      </div>
    )
  }

  const trackingEvents = getTrackingEvents()

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Truck className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-primary">Update Tracking</h1>
        </div>
        <Link to={`/admin/orders/${order?.orderId || order?.order_id || orderId}`}>
          <Button
            variant="outline"
            className="border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Order Details
          </Button>
        </Link>
      </div>
      <p className="text-gray-600">
        Add shipment tracking for order <span className="font-semibold text-primary">{order?.orderId || order?.order_id || orderId}</span>.
      </p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Add New Shipment Form */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Add Shipment</CardTitle>
              <CardDescription>Add tracking information for this order.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddShipment} className="space-y-4">
                <div>
                  <Label htmlFor="trackingNumber">Tracking Number *</Label>
                  <Input
                    id="trackingNumber"
                    placeholder="e.g., GLOW123456789"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    required
                    disabled={addingShipment}
                  />
                </div>
                <div>
                  <Label htmlFor="courier">Courier *</Label>
                  <Select value={courier} onValueChange={setCourier} required disabled={addingShipment}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select courier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DHL">DHL</SelectItem>
                      <SelectItem value="FedEx">FedEx</SelectItem>
                      <SelectItem value="UPS">UPS</SelectItem>
                      <SelectItem value="GIGL">GIGL</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="courierService">Courier Service (Optional)</Label>
                  <Input
                    id="courierService"
                    placeholder="e.g., Express, Standard"
                    value={courierService}
                    onChange={(e) => setCourierService(e.target.value)}
                    disabled={addingShipment}
                  />
                </div>
                <div>
                  <Label htmlFor="estimatedDelivery">Estimated Delivery (Optional)</Label>
                  <Input
                    id="estimatedDelivery"
                    type="date"
                    value={estimatedDelivery}
                    onChange={(e) => setEstimatedDelivery(e.target.value)}
                    disabled={addingShipment}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                  disabled={addingShipment || !trackingNumber.trim() || !courier.trim()}
                >
                  {addingShipment ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" /> Adding...
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-4 h-4 mr-2" /> Add Shipment
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Tracking Timeline */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Tracking History</CardTitle>
              <CardDescription>All events related to this order's shipment and status.</CardDescription>
            </CardHeader>
            <CardContent>
              {trackingEvents.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No tracking events yet. Add a shipment to get started.</p>
              ) : (
                <ol className="relative border-l border-gray-200 ml-4">
                  {trackingEvents.map((event, index) => (
                    <li key={index} className="mb-8 ml-6">
                      <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-secondary ring-8 ring-white">
                        {event.type === "shipment" ? (
                          <Truck className="h-4 w-4 text-primary" />
                        ) : (
                          <ClipboardCopy className="h-4 w-4 text-primary" />
                        )}
                      </span>
                      <h3 className="mb-1 flex items-center text-lg font-semibold text-primary">
                        {event.title}
                      </h3>
                      <time className="block mb-2 text-sm font-normal leading-none text-gray-500">
                        <Clock className="inline-block w-4 h-4 mr-1 align-text-bottom" />
                        {formatDateTime(event.timestamp)}
                      </time>
                      <p className="text-base font-normal text-gray-700 mb-1">{event.description}</p>
                      {event.location && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </p>
                      )}
                      {event.estimatedDelivery && (
                        <p className="text-xs text-gray-500 mt-1">
                          Estimated delivery: {formatDateTime(event.estimatedDelivery)}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
